/**
 * Shared fetch helper for Sentinel UI screens.
 *
 * Every `createFetch<Screen>Api()` factory (see `src/screens/<screen>/api.fetch.ts`)
 * composes `apiFetch()` + `withQuery()` and nothing else. The helper is
 * deliberately tiny — no Axios, no SWR plumbing — because ADR-0013 pins this
 * SPA to a Node-runtime-free dist served by the Rust gateway.
 *
 *   - All requests use `credentials: 'include'` so the `sentinel-api` session
 *     cookie round-trips on same-origin calls (the dev server proxies `/api`
 *     to `localhost:8080`; production serves the SPA from the gateway).
 *   - When a session is present (see `@/app/session`), the bearer token is
 *     attached as `Authorization: Bearer …` so `sentinel-api`'s
 *     `require_bearer` middleware accepts the request (Blocker-1 fix, see
 *     `docs/review/deep-review-8f9dc7a.md`).
 *   - A `401 Unauthorized` clears the session and redirects to `/auth` with
 *     a `returnTo` query param so the operator lands back where they were
 *     after re-authenticating.
 *   - Non-2xx responses throw `ApiError` with the status + raw body so the
 *     `GlobalErrorBanner` in `Layout.tsx` can surface the failure.
 *   - An empty body (204 / explicit `Content-Length: 0`) resolves as `null`,
 *     mirroring the mock factories that return `null` for "no row".
 */

import { clearSession, peekSession, subscribeSession } from './session';

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// Keep the most recent bearer in a module-local binding so `apiFetch`
// doesn't have to hit IndexedDB on every call. `subscribeSession`
// pushes updates from `putSession` / `clearSession`.
let cachedBearer: string | null = null;
subscribeSession((s) => {
  cachedBearer = s && s.token ? s.token : null;
});

/**
 * Fire `fetch` with JSON defaults and return the parsed body. Throws
 * {@link ApiError} on any non-2xx response. On 401 clears the session
 * and bounces the user to `/auth`.
 */
export async function apiFetch<T>(input: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(toHeaderRecord(init.headers) ?? {}),
  };

  // Prefer the sync-cached bearer, but fall back to the live session
  // peek to cover the race where `subscribeSession` hasn't emitted yet
  // (first-call-after-signin).
  const bearer = cachedBearer ?? peekSession()?.token ?? null;
  if (bearer && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${bearer}`;
  }

  const res = await fetch(input, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    // Bearer missing / expired / rejected. Clear the local session and
    // bounce to the sign-in screen with a `returnTo` pointer so the
    // operator lands back here. Skip the redirect inside Vitest
    // (no `window.location.assign` equivalent) — the thrown ApiError
    // still propagates so callers can test the 401 path.
    await clearSession();
    if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
      const here = `${window.location.pathname}${window.location.search}`;
      const alreadyOnAuth = here.startsWith('/auth');
      if (!alreadyOnAuth) {
        window.location.assign(`/auth?returnTo=${encodeURIComponent(here)}`);
      }
    }
    const body = await safeReadBody(res);
    throw new ApiError(401, body, `${(init.method ?? 'GET').toUpperCase()} ${input} -> 401`);
  }

  if (!res.ok) {
    const body = await safeReadBody(res);
    throw new ApiError(
      res.status,
      body,
      `${(init.method ?? 'GET').toUpperCase()} ${input} -> ${res.status}`,
    );
  }

  // 204 / empty → null (screens treat it as "no data").
  const text = await res.text();
  if (!text) return null as T;
  return JSON.parse(text) as T;
}

/**
 * Normalise a `HeadersInit` into the plain record shape `apiFetch`
 * works with so spreads compose predictably.
 */
function toHeaderRecord(h: HeadersInit | undefined): Record<string, string> | undefined {
  if (!h) return undefined;
  if (h instanceof Headers) {
    const out: Record<string, string> = {};
    h.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  }
  if (Array.isArray(h)) {
    return Object.fromEntries(h);
  }
  return { ...(h as Record<string, string>) };
}

async function safeReadBody(res: Response): Promise<unknown> {
  try {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  } catch {
    return null;
  }
}

/**
 * Append a URLSearchParams string to `base`, skipping `undefined` values so
 * callers can hand us sparse query objects without an `exactOptionalProperty`
 * compile error.
 */
export function withQuery(
  base: string,
  q: Record<string, string | number | boolean | undefined>,
): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
